import { VStack } from '@chakra-ui/react';
import { Tooltip } from '../../../../../components/ui/tooltip';
import {
  CustomerPortalContactUpdate,
  CustomerPortalContactUpdateRequest,
  IAddressSchema,
} from '@monetize/types/app';
import lodashGet from 'lodash/get';
import { useMemo } from 'react';
import { Controller, FieldErrors, UseFormReturn } from 'react-hook-form';
import {
  MFormField,
  MGrid,
  MGridItem,
  MInput,
} from '../../../../../components/Monetize';
import { MainSearchInputV2 } from '../../../../../components/Monetize/MCustomSelect/components/MainSearchInputV2';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import {
  countriesWithStates,
  getStatesFromCountry,
} from '../../../../../services/countryState';

interface ContactsCardBodyEditProps {
  formState: UseFormReturn<CustomerPortalContactUpdateRequest>;
  formKeyPrefix: 'billingContact' | 'shippingContact';
  isDisabled?: boolean;
}

function getError(
  errors: FieldErrors<CustomerPortalContactUpdateRequest>,
  formKeyPrefix: 'billingContact' | 'shippingContact',
  field: keyof CustomerPortalContactUpdate | `address.${keyof IAddressSchema}`,
) {
  return lodashGet(errors, `${formKeyPrefix}.${field}`);
}

export const ContactsCardBodyEdit = ({
  formState,
  formKeyPrefix,
  isDisabled,
}: ContactsCardBodyEditProps) => {
  const {
    control,
    formState: { errors },
    watch,
  } = formState;

  const selectedCountry = watch(`${formKeyPrefix}.address.country`);

  const { stateFormat, states } = useMemo(() => {
    const _states = getStatesFromCountry(selectedCountry);
    return {
      states: _states,
      stateFormat: _states?.length > 0 ? 'select' : 'input',
    };
  }, [selectedCountry]);

  return (
    <VStack gap={4} align="stretch">
      <MFormField
        error={getError(errors, formKeyPrefix, 'fullName')}
        label="Name"
        isRequired
      >
        <Controller
          name={`${formKeyPrefix}.fullName`}
          control={control}
          defaultValue=""
          render={({ field }) => <MInput {...field} isDisabled={isDisabled} />}
        />
      </MFormField>

      <MFormField
        error={getError(errors, formKeyPrefix, 'email')}
        label="Email"
        isRequired
      >
        <Controller
          name={`${formKeyPrefix}.email`}
          control={control}
          defaultValue=""
          render={({ field }) => <MInput {...field} isDisabled={isDisabled} />}
        />
      </MFormField>

      <MFormField
        error={getError(errors, formKeyPrefix, 'phone')}
        label="Phone"
      >
        <Controller
          name={`${formKeyPrefix}.phone`}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <MInput
              {...field}
              value={field.value || ''}
              isDisabled={isDisabled}
            />
          )}
        />
      </MFormField>

      <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
        <MGridItem colSpan={12}>
          <MFormField
            error={getError(errors, formKeyPrefix, 'address.country')}
            label="Country"
          >
            <Controller
              name={`${formKeyPrefix}.address.country`}
              control={control}
              defaultValue=""
              render={({ field: { value, onChange, ...rest } }) => (
                <MCustomSelect
                  useMainInputAsSearchInput
                  MainInputComponent={MainSearchInputV2}
                  useHighlightedItemList
                  items={countriesWithStates}
                  itemTitle="name"
                  itemValue="isoCode"
                  value={value || ''}
                  clearable
                  onChange={(ev) => {
                    onChange(ev);
                  }}
                  useRawValue
                  isDisabled={isDisabled}
                  {...rest}
                />
              )}
            />
          </MFormField>
        </MGridItem>

        <MGridItem colSpan={[12, 12, 12, 6]}>
          <MFormField
            error={getError(errors, formKeyPrefix, 'address.line1')}
            label="Address Line 1"
          >
            <Controller
              name={`${formKeyPrefix}.address.line1`}
              control={control}
              defaultValue=""
              render={({ field: { value, ...rest } }) => (
                <MInput
                  value={value || ''}
                  {...rest}
                  maxLength={255}
                  isDisabled={isDisabled}
                />
              )}
            />
          </MFormField>
        </MGridItem>

        <MGridItem colSpan={[12, 12, 12, 6]}>
          <MFormField
            error={getError(errors, formKeyPrefix, 'address.line2')}
            label="Address Line 2"
          >
            <Controller
              name={`${formKeyPrefix}.address.line2`}
              control={control}
              defaultValue=""
              render={({ field: { value, ...rest } }) => (
                <MInput
                  value={value || ''}
                  {...rest}
                  maxLength={255}
                  isDisabled={isDisabled}
                />
              )}
            />
          </MFormField>
        </MGridItem>

        <MGridItem colSpan={[12, 12, 4]}>
          <MFormField
            error={getError(errors, formKeyPrefix, 'address.city')}
            label="City"
          >
            <Controller
              name={`${formKeyPrefix}.address.city`}
              control={control}
              defaultValue=""
              render={({ field: { value, ...rest } }) => (
                <MInput value={value || ''} {...rest} isDisabled={isDisabled} />
              )}
            />
          </MFormField>
        </MGridItem>

        <MGridItem colSpan={[12, 12, 4]}>
          <MFormField
            error={getError(errors, formKeyPrefix, 'address.state')}
            label="State/Province"
          >
            <Controller
              name={`${formKeyPrefix}.address.state`}
              control={control}
              defaultValue=""
              render={({ field: { value, ...rest } }) =>
                stateFormat === 'select' ? (
                  <MCustomSelect
                    useMainInputAsSearchInput
                    MainInputComponent={MainSearchInputV2}
                    useHighlightedItemList
                    items={states}
                    itemTitle="name"
                    itemValue="isoCode"
                    value={value || ''}
                    clearable
                    useRawValue
                    isDisabled={isDisabled}
                    {...rest}
                  />
                ) : (
                  <Tooltip
                    disabled={!!selectedCountry}
                    content="Choose a country to see available states"
                  >
                    <MInput
                      value={value || ''}
                      {...rest}
                      isDisabled={!selectedCountry || isDisabled}
                      placeholder={
                        !selectedCountry
                          ? 'Select country first'
                          : 'Enter state/province'
                      }
                    />
                  </Tooltip>
                )
              }
            />
          </MFormField>
        </MGridItem>

        <MGridItem colSpan={[12, 12, 4]}>
          <MFormField
            error={getError(errors, formKeyPrefix, 'address.postalCode')}
            label="Postal Code"
          >
            <Controller
              name={`${formKeyPrefix}.address.postalCode`}
              control={control}
              defaultValue=""
              render={({ field: { value, ...rest } }) => (
                <MInput value={value || ''} {...rest} isDisabled={isDisabled} />
              )}
            />
          </MFormField>
        </MGridItem>
      </MGrid>
    </VStack>
  );
};
